/**
 * Finish visit without leaving the page (Today page / My Today): the short
 * Visit log a presenter fills after a front desk visit. Saves through the
 * same `logLeadActivity` callable used for lead activity,
 * so the timeline, visit count, reception finish and 7 PM thank-you all
 * behave the same.
 *
 * Desktop: centred dialog. Phone: bottom sheet.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Dialog, SwipeableDrawer, TextField, Typography, useMediaQuery } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { CAME_VIA_OPTIONS, minutesSince, toDate } from '../utils/reception';
import type { ReceptionVisit, VisitSourceType } from '../utils/reception';
import { compareUnitNumbers } from '../utils/unitSort';

const GOLD = '#9d6f32';
const DARK = '#1c1b19';
const MUTED = '#5b5b5b';
const LINE = '#d9d3c8';
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const fieldSx = { width: '100%', boxSizing: 'border-box', height: 44, fontFamily: FONT, fontSize: 14, px: 1.5, border: `1px solid ${LINE}`, borderRadius: 2.5, bgcolor: '#fff' };

// Default status after a visit; configured next statuses and close reasons appear below.
const SITE_VISITED = 'GqoIk5tdthxSlgxG0hm3';
const CLOSE_REASONS = [
  'Completed', 'Not Interested', 'Interested in Another Location', 'Purchased in Another Location',
  'Need Other Type', 'Budget / Financial Issue', 'Distance Issue', 'Not Reachable (No Response)',
  'Wrong / Invalid Number', 'Broker Inquiry Only', 'Loss due to Competitor', 'Other',
];
const CLOSE_SECONDARY_REASONS = [
  'Not Interested', 'Interested in Another Location', 'Budget / Financial Issue', 'Distance Issue',
  'Not Reachable (No Response)', 'Wrong / Invalid Number', 'Broker Inquiry Only', 'Loss due to Competitor',
  'Trust / Brand Concern', 'Unit / Layout Mismatch', 'Possession Timeline Mismatch', 'Other',
];
const CLOSE_STAGES = ['New Lead', 'Connected Call', 'Follow-up in Progress', 'Site Visit Done', 'Negotiation Stage', 'Booking Stage'];
const stageForStatus = (raw: string) => {
  const status = raw.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (status.includes('book')) return 'Booking Stage';
  if (status.includes('negotiat')) return 'Negotiation Stage';
  if (status.includes('site visit') || status.includes('visit done')) return 'Site Visit Done';
  if (['qualify', 'appointment', 'prospect', 'cnr', 'reopen'].some((value) => status.includes(value))) return 'Follow-up in Progress';
  if (status.includes('connect') || status.includes('call')) return 'Connected Call';
  if (status.includes('new')) return 'New Lead';
  return '';
};
const CLOSE_BUDGET_DETAILS = ['Budget mismatch', 'Loan not possible', 'Other budget-related issue'];
const CLOSE_REQUIRED_TYPES = ['1 BHK', '2 BHK', '3 BHK', '4 BHK+', 'Row House', 'Open Plot', 'Shop'];
const CLOSE_COMP_PRICE_BANDS = ['Similar', 'Up to 5L cheaper', '5L-10L cheaper', '10L+ cheaper'];
const CLOSE_COMP_ADVANTAGES = ['Lower price', 'Better location', 'Better layout', 'Faster possession', 'Better amenities', 'Better payment plan', 'Broker influence', 'Other'];
const INTEREST: Array<{ key: number; label: string; color: string }> = [
  { key: 4, label: 'Hot', color: '#b3261e' },
  { key: 3, label: 'Warm', color: '#c77700' },
  { key: 2, label: 'Cold', color: '#2f6b9a' },
];

// Callable payload tokens understood by logLeadActivity.
const serverTs = () => ({ __op: 'serverTimestamp' as const });
const inc = (by: number) => ({ __op: 'increment' as const, by });
const ts = (d: Date) => ({ __type: 'timestamp', millis: d.getTime() });

const dateInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const displayDate = (value: string) => {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? 'Choose a date' : new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};
const answer = (raw: any): string => String(raw?.value ?? raw ?? '').trim();
const answerList = (raw: any): string[] => {
  const value = raw?.value ?? raw;
  return (Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [])
    .map((item: unknown) => String(item || '').trim()).filter(Boolean);
};
const statusIsClosed = (value: string) => ['closed', 'completed'].includes(value.trim().toLowerCase());

const Pill = ({ selected, onClick, children, color = GOLD }: { selected: boolean; onClick: () => void; children: React.ReactNode; color?: string }) => (
  <Box component="button" type="button" onClick={onClick} aria-pressed={selected}
    sx={{
      minHeight: 42, px: 2, borderRadius: 999, cursor: 'pointer', fontFamily: FONT, fontSize: 14,
      fontWeight: selected ? 700 : 500, border: selected ? 'none' : `1px solid ${LINE}`,
      bgcolor: selected ? color : '#fff', color: selected ? '#fff' : '#1a1a1a',
    }}>
    {children}
  </Box>
);

const Label = ({ children, htmlFor, previous = false }: { children: React.ReactNode; htmlFor?: string; previous?: boolean }) => (
  <Typography component={htmlFor ? 'label' : 'span'} {...(htmlFor ? { htmlFor } : {})}
    sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: MUTED, display: 'block', mb: 0.75 }}>
    {children}{previous && <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.25, borderRadius: 1,
      bgcolor: '#f3efe8', color: GOLD, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>Previously recorded</Box>}
  </Typography>
);

type Props = {
  open: boolean;
  leadId: string | null;
  visit: ReceptionVisit | null;
  /** Managers and admins may set the rating (Interest). */
  canRate: boolean;
  onClose: () => void;
  /** Open the full lead for additional history and actions. */
  onOpenLead: (leadId: string) => void;
};

type VisitBrokerOption = { id: string; brokerName: string; agencyName: string; label: string };

export default function FinishVisitDialog({ open, leadId, visit, canRate, onClose, onOpenLead }: Props) {
  const { currentUser } = useAuth();
  const isPhone = useMediaQuery('(max-width: 599px)');
  const [leadSnapshot, setLeadSnapshot] = useState<{ id: string; data: Record<string, any> } | null>(null);
  const lead = leadSnapshot?.id === leadId ? leadSnapshot.data : null;
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [unitNumbers, setUnitNumbers] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<Array<{ id: string; label: string; allowedNext?: string[]; prompts?: Array<{ id: string; label: string; type: string; options?: string[] }> }>>([]);
  const [unitType, setUnitType] = useState('');
  const [budget, setBudget] = useState('');
  const [floorChoice, setFloorChoice] = useState('');
  const [unitChoices, setUnitChoices] = useState<string[]>([]);
  const [unitSearch, setUnitSearch] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [residence, setResidence] = useState('');
  const [occupation, setOccupation] = useState('');
  const [promptDraft, setPromptDraft] = useState<Record<string, string>>({});
  const [rating, setRating] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [points, setPoints] = useState<string[]>([]);
  const [visitSourceType, setVisitSourceType] = useState<VisitSourceType>('unknown');
  const [visitSourceBrokers, setVisitSourceBrokers] = useState<VisitBrokerOption[]>([]);
  const [visitSourceBroker, setVisitSourceBroker] = useState<VisitBrokerOption | null>(null);
  const [visitSourceReferrerName, setVisitSourceReferrerName] = useState('');
  const [visitSourceNote, setVisitSourceNote] = useState('');
  const [sourceEditing, setSourceEditing] = useState(false);
  const [sourceTouched, setSourceTouched] = useState(false);

  const addPoint = (text: string) => {
    const t = text.trim().replace(/\s+/g, ' ');
    if (!t) return;
    setPoints((prev) => (prev.some((p) => p.toLowerCase() === t.toLowerCase()) ? prev : [...prev, t]));
  };
  const [status, setStatus] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [closeOtherReason, setCloseOtherReason] = useState('');
  const [closeStage, setCloseStage] = useState('');
  const [closeStageTouched, setCloseStageTouched] = useState(false);
  const [closeSecondaryReasons, setCloseSecondaryReasons] = useState<string[]>([]);
  const [closeDetail, setCloseDetail] = useState('');
  const [closeRequiredType, setCloseRequiredType] = useState('');
  const [closeCompetitorProject, setCloseCompetitorProject] = useState('');
  const [closeCompetitorPriceBand, setCloseCompetitorPriceBand] = useState('');
  const [closeCompetitorAdvantage, setCloseCompetitorAdvantage] = useState('');
  const [assignToMe, setAssignToMe] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !leadId) { setLeadSnapshot(null); return undefined; }
    setLeadSnapshot(null);
    return onSnapshot(doc(db, 'leads', leadId), (snap) => setLeadSnapshot(snap.exists() ? { id: leadId, data: snap.data() } : null), () => setLeadSnapshot(null));
  }, [open, leadId]);

  const projectId = String(visit?.projectId || lead?.projectId || '');
  useEffect(() => {
    if (!open || !projectId) { setUnitTypes([]); return; }
    setUnitTypes([]);
    getDoc(doc(db, 'projects', projectId))
      .then((snap) => {
        const raw = (snap.data() || {}).unitTypes;
        setUnitTypes(Array.isArray(raw) ? raw.map((u: any) => String(typeof u === 'string' ? u : u?.name || u?.label || '')).filter(Boolean) : []);
      })
      .catch(() => setUnitTypes([]));
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !projectId) { setUnitNumbers([]); return undefined; }
    setUnitNumbers([]);
    return onSnapshot(query(collection(db, 'projects', projectId, 'units'), limit(1000)),
      (snap) => setUnitNumbers(Array.from(new Set(snap.docs.map((item) => String(item.data().unitNumber || '').trim()).filter(Boolean))).sort(compareUnitNumbers)),
      () => setUnitNumbers([]));
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return undefined;
    return onSnapshot(collection(db, 'leadStatuses'),
      (snap) => setStatuses(snap.docs.map((item) => ({ id: item.id, ...item.data() } as any))),
      () => setStatuses([]));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    getDocs(query(collection(db, 'brokers'), limit(500))).then((brokerSnap) => {
      if (!active) return;
      setVisitSourceBrokers(brokerSnap.docs.map((item) => {
        const data = item.data();
        const brokerName = String(data.brokerName || data.displayName || '').trim();
        const agencyName = String(data.agencyName || '').trim();
        return { id: item.id, brokerName, agencyName, label: [agencyName, brokerName].filter(Boolean).join(' · ') || item.id };
      }).filter((item) => !!item.brokerName || !!item.agencyName));
    }).catch(() => {
      if (active) setVisitSourceBrokers([]);
    });
    return () => { active = false; };
  }, [open]);

  // Fresh form each time it opens, filled from the lead.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNote('');
    setPoints([]);
    setAssignToMe(null);
    setStatus(SITE_VISITED);
    setUnitType(''); setBudget(''); setFloorChoice(''); setUnitChoices([]); setUnitSearch('');
    setResidence(''); setOccupation(''); setPromptDraft({}); setRating(null);
    setCloseReason(''); setCloseOtherReason(''); setCloseStage(''); setCloseStageTouched(false); setCloseSecondaryReasons([]); setCloseDetail('');
    setCloseRequiredType(''); setCloseCompetitorProject(''); setCloseCompetitorPriceBand(''); setCloseCompetitorAdvantage('');
    setFollowUpTime('');
    setFollowUp('');
    const correctionTo = visit?.sourceCorrection?.to || {};
    const sourceType = String(correctionTo.sourceType || (visit?.cameVia === 'broker' ? 'broker' : visit?.cameVia === 'c2c' ? 'c2c' : visit?.cameVia === 'walk-in' ? 'direct' : 'unknown')) as VisitSourceType;
    setVisitSourceType(sourceType);
    const brokerId = String(correctionTo.brokerId || (visit?.cameVia === 'broker' ? visit.brokerId || '' : ''));
    const brokerName = String(correctionTo.brokerName || (visit?.cameVia === 'broker' ? visit.brokerName || '' : ''));
    const agencyName = String(correctionTo.brokerAgencyName || (visit?.cameVia === 'broker' ? visit.agencyName || '' : ''));
    setVisitSourceBroker(sourceType === 'broker' && (brokerId || brokerName || agencyName)
      ? { id: brokerId, brokerName, agencyName, label: [agencyName, brokerName].filter(Boolean).join(' · ') || brokerId }
      : null);
    setVisitSourceReferrerName(String(correctionTo.customerReferrerName || (visit?.cameVia === 'c2c' ? visit.c2cName || '' : '')));
    setVisitSourceNote(String(correctionTo.note || ''));
    setSourceEditing(false);
    setSourceTouched(false);
    const today = new Date();
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, visit?.id]);

  // Keep an open form in sync if an admin corrects the active visit elsewhere.
  useEffect(() => {
    if (!open || !visit || sourceTouched) return;
    const corrected = visit.sourceCorrection?.to || {};
    const sourceType = String(corrected.sourceType || (visit.cameVia === 'broker' ? 'broker' : visit.cameVia === 'c2c' ? 'c2c' : visit.cameVia === 'walk-in' ? 'direct' : 'unknown')) as VisitSourceType;
    setVisitSourceType(sourceType);
    const brokerId = String(corrected.brokerId || (visit.cameVia === 'broker' ? visit.brokerId || '' : ''));
    const brokerName = String(corrected.brokerName || (visit.cameVia === 'broker' ? visit.brokerName || '' : ''));
    const agencyName = String(corrected.brokerAgencyName || (visit.cameVia === 'broker' ? visit.agencyName || '' : ''));
    setVisitSourceBroker(sourceType === 'broker' && (brokerId || brokerName || agencyName)
      ? { id: brokerId, brokerName, agencyName, label: [agencyName, brokerName].filter(Boolean).join(' · ') || brokerId }
      : null);
    setVisitSourceReferrerName(String(corrected.customerReferrerName || (visit.cameVia === 'c2c' ? visit.c2cName || '' : '')));
    setVisitSourceNote(String(corrected.note || ''));
  }, [open, visit, sourceTouched]);

  useEffect(() => {
    if (!open || !lead) return;
    setUnitType((v) => v || (visit?.projectId && lead.projectId && visit.projectId !== lead.projectId ? '' : String(lead.unitType || '')));
    setBudget((v) => v || answer(lead.promptAnswers?.legacy_budget));
    setFloorChoice((v) => v || answer(lead.promptAnswers?.legacy_floorChoice));
    setUnitChoices((v) => v.length ? v : (visit?.projectId && lead.projectId && visit.projectId !== lead.projectId ? [] : answerList(lead.promptAnswers?.legacy_unitChoice)));
    setResidence((v) => v || answer(lead.promptAnswers?.legacy_currentResidence));
    setOccupation((v) => v || answer(lead.promptAnswers?.legacy_occupation));
    const previousClose = lead.closedAnalysis || {};
    const previousReason = String(lead.closePrimaryReason || previousClose.primaryReasonBase || '');
    setCloseReason((v) => v || previousReason);
    setCloseOtherReason((v) => v || String(lead.closePrimaryOtherReason || previousClose.primaryOtherReason || ''));
    setCloseSecondaryReasons((v) => v.length ? v : (Array.isArray(lead.closeSecondaryReasons) ? lead.closeSecondaryReasons : previousClose.secondaryReasons || []));
    const previousDetails: Record<string, any> = {
      'Interested in Another Location': lead.closeInterestedLocation || previousClose.interestedLocation,
      'Purchased in Another Location': lead.closeBoughtAtLocation || previousClose.boughtAtLocation,
      'Need Other Type': lead.closeRequiredTypeLocation || previousClose.requiredTypeLocation,
      'Budget / Financial Issue': lead.closeBudgetDetail || previousClose.budgetDetail,
      'Distance Issue': lead.closeDistancePreferredArea || previousClose.distancePreferredArea,
    };
    setCloseDetail((v) => v || String(previousDetails[previousReason] || ''));
    setCloseRequiredType((v) => v || String(lead.closeRequiredType || previousClose.requiredType || ''));
    setCloseCompetitorProject((v) => v || String(lead.closeCompetitorProject || previousClose.competitorProject || ''));
    setCloseCompetitorPriceBand((v) => v || String(lead.closeCompetitorPriceBand || previousClose.competitorPriceBand || ''));
    setCloseCompetitorAdvantage((v) => v || String(lead.closeCompetitorAdvantage || previousClose.competitorAdvantage || ''));
    setRating((v) => (v === null ? (typeof lead.rating === 'number' ? lead.rating : null) : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId, !!lead]);

  useEffect(() => {
    if (!unitTypes.length) return;
    setUnitType((value) => value && !unitTypes.includes(value) ? '' : value);
  }, [unitTypes]);

  useEffect(() => {
    if (!unitNumbers.length) return;
    setUnitChoices((choices) => choices.filter((value) => unitNumbers.includes(value)));
  }, [unitNumbers]);

  const me = String(currentUser?.email || '').toLowerCase();
  const owner = String(lead?.assignedTo || '').toLowerCase();
  const [ownerLookup, setOwnerLookup] = useState('');
  useEffect(() => {
    setOwnerLookup('');
    if (!owner || lead?.assignedToName) return;
    getDocs(query(collection(db, 'users'), where('email', '==', owner), limit(1)))
      .then((snap) => {
        const u = snap.docs[0]?.data() as any;
        setOwnerLookup(String(u?.displayName || u?.name || ''));
      })
      .catch(() => undefined);
  }, [owner, lead?.assignedToName]);
  const ownerName = String(lead?.assignedToName || ownerLookup || lead?.assignedTo || '');
  const askOwner = !!owner && owner !== me && !!visit && String(visit.presenterEmail || '').toLowerCase() === me;
  const currentStatus = statuses.find((item) => item.id === lead?.status || item.label.trim().toLowerCase() === String(lead?.status || '').trim().toLowerCase())
    || (String(lead?.status || '').trim().toLowerCase() === 'new'
      ? statuses.find((item) => item.label.trim().toLowerCase().startsWith('new')) : undefined);
  const allowedStatuses = currentStatus ? new Set([currentStatus.id, ...(currentStatus.allowedNext || [])]) : null;
  const statusChoices = useMemo(() => {
    const current = String(lead?.status || '');
    return [
      { id: '', label: `Keep ${currentStatus?.label || String(lead?.status || 'current status')}` },
      ...statuses.filter((item) => item.id !== (currentStatus?.id || current) && (!allowedStatuses || allowedStatuses.has(item.id)))
        .map((item) => ({ id: item.id, label: item.label })),
    ];
  }, [lead?.status, statuses, currentStatus?.id, currentStatus?.label]);
  const savedCloseStage = String(lead?.closeDecisionStage || lead?.closedAnalysis?.decisionStage || lead?.closeDecisionStageAuto || lead?.closedAnalysis?.decisionStageAuto || '');
  const currentStatusLabel = currentStatus?.label || String(lead?.status || '');
  const autoCloseStage = statusIsClosed(currentStatusLabel)
    ? savedCloseStage || 'Site Visit Done'
    : stageForStatus(currentStatusLabel) || savedCloseStage || (visit ? 'Site Visit Done' : 'New Lead');
  useEffect(() => {
    if (!open || !lead || closeStageTouched) return;
    setCloseStage(autoCloseStage);
  }, [open, lead, closeStageTouched, autoCloseStage]);
  useEffect(() => {
    if (open && lead && status === SITE_VISITED && statuses.length &&
        !statusChoices.some((choice) => choice.id === SITE_VISITED)) {
      setStatus('');
      setError(null);
    }
  }, [open, lead, status, statuses, statusChoices]);
  const selectedStatus = statusChoices.some((item) => item.id === status) ? status : '';
  const selectedStatusDoc = selectedStatus ? statuses.find((item) => item.id === selectedStatus) : currentStatus;
  const isClosing = statusIsClosed(selectedStatusDoc?.label || selectedStatusDoc?.id || '');
  const statusPrompts = (selectedStatusDoc?.prompts || []).filter((item) =>
    !['close reason', 'occupation', 'current residence'].includes(String(item.label || '').toLowerCase()));
  const sameLeadProject = !visit?.projectId || !lead?.projectId || visit.projectId === lead.projectId;
  const previousUnitType = sameLeadProject ? String(lead?.unitType || '') : '';
  const previousBudget = answer(lead?.promptAnswers?.legacy_budget);
  const previousFloorChoice = answer(lead?.promptAnswers?.legacy_floorChoice);
  const previousUnitChoices = sameLeadProject ? answerList(lead?.promptAnswers?.legacy_unitChoice) : [];
  const previousResidence = answer(lead?.promptAnswers?.legacy_currentResidence);
  const previousOccupation = answer(lead?.promptAnswers?.legacy_occupation);
  const hasPreviousAnswers = !!(previousUnitType || previousBudget || previousFloorChoice ||
    previousUnitChoices.length || previousResidence || previousOccupation ||
    statusPrompts.some((prompt) => lead?.promptAnswers?.[prompt.id] != null));

  useEffect(() => {
    if (!open || !lead || !statusPrompts.length) return;
    setPromptDraft((draft) => {
      const next = { ...draft };
      for (const prompt of statusPrompts) {
        if (next[prompt.id] !== undefined) continue;
        const existing = lead.promptAnswers?.[prompt.id];
        if (existing === undefined || existing === null) continue;
        const value = existing?.value ?? existing;
        next[prompt.id] = prompt.type === 'date' ? (toDate(value) ? dateInput(toDate(value)!) : '') : String(value ?? '');
      }
      return next;
    });
  }, [open, lead, selectedStatusDoc?.id]);

  const save = async () => {
    if (!leadId || !lead || !currentUser?.email) return;
    setError(null);
    if (!unitType.trim() || !unitTypes.includes(unitType)) return setError('Choose a unit type for this project.');
    if (status && !statusChoices.some((item) => item.id === status)) return setError('Choose a valid next status.');
    if (!isClosing && !followUp) return setError('Set the next follow-up date.');
    const follow = new Date(`${followUp}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!isClosing && (Number.isNaN(follow.getTime()) || follow <= today)) return setError('Next follow-up must be after today.');
    const followAt = !isClosing && followUpTime ? new Date(`${followUp}T${followUpTime}:00`) : null;
    if (followAt && (Number.isNaN(followAt.getTime()) || followAt.getTime() < Date.now())) return setError('Reminder time must be in the future.');
    if (isClosing) {
      if (!closeReason) return setError('Choose a close reason.');
      if (closeReason === 'Other' && !closeOtherReason.trim()) return setError('Describe the close reason.');
      if (closeReason !== 'Completed' && !closeStage) return setError('Choose the decision stage.');
      if (['Interested in Another Location', 'Purchased in Another Location', 'Distance Issue'].includes(closeReason) && !closeDetail.trim()) return setError('Enter the location or preferred area.');
      if (closeReason === 'Need Other Type' && (!closeRequiredType || !closeDetail.trim())) return setError('Select the required type and location.');
      if (closeReason === 'Budget / Financial Issue' && !closeDetail) return setError('Choose the budget issue.');
      if (closeReason === 'Loss due to Competitor' && (!closeCompetitorProject.trim() || !closeCompetitorPriceBand || !closeCompetitorAdvantage)) return setError('Complete the competitor details.');
    }
    if (askOwner && assignToMe === null) return setError(`Choose: assign to you, or keep with ${ownerName}.`);
    if (sourceTouched && visitSourceType === 'broker' && !visitSourceBroker) return setError('Choose the correct broker for this visit.');
    if (sourceTouched && visitSourceType === 'c2c' && !visitSourceReferrerName.trim()) return setError('Enter the customer who referred this visit.');
    if (sourceTouched && visitSourceType === 'other' && !visitSourceNote.trim()) return setError('Add a short note for the visit source.');
    for (const prompt of statusPrompts) {
      const raw = (promptDraft[prompt.id] || '').trim();
      if (!raw) continue;
      if (prompt.type === 'number' && Number.isNaN(Number(raw))) return setError(`Invalid answer for ${prompt.label}.`);
      if (prompt.type === 'date' && Number.isNaN(new Date(`${raw}T00:00:00`).getTime())) return setError(`Invalid answer for ${prompt.label}.`);
    }

    setSaving(true);
    const by = currentUser.email;
    try {
      const originalSourceType: VisitSourceType = visit?.cameVia === 'broker' ? 'broker' : visit?.cameVia === 'c2c' ? 'c2c' : visit?.cameVia === 'walk-in' ? 'direct' : 'unknown';
      const originalSource = {
        sourceType: originalSourceType,
        brokerId: visit?.cameVia === 'broker' ? visit.brokerId : null,
        brokerName: visit?.cameVia === 'broker' ? visit.brokerName : null,
        brokerAgencyName: visit?.cameVia === 'broker' ? visit.agencyName : null,
        customerReferrerName: visit?.cameVia === 'c2c' ? visit.c2cName : null,
        note: null,
      };
      const fromSource = visit?.sourceCorrection?.to || originalSource;
      const toSource: { sourceType: VisitSourceType; brokerId: string | null; brokerName: string | null; brokerAgencyName: string | null; customerReferrerLeadId: string | null; customerReferrerName: string | null; note: string | null } = {
        sourceType: sourceTouched ? visitSourceType : String(fromSource.sourceType) as VisitSourceType,
        brokerId: sourceTouched ? (visitSourceType === 'broker' ? visitSourceBroker?.id || null : null) : String(fromSource.brokerId || '') || null,
        brokerName: sourceTouched ? (visitSourceType === 'broker' ? visitSourceBroker?.brokerName || null : null) : String(fromSource.brokerName || '') || null,
        brokerAgencyName: sourceTouched ? (visitSourceType === 'broker' ? visitSourceBroker?.agencyName || null : null) : String(fromSource.brokerAgencyName || '') || null,
        customerReferrerLeadId: null,
        customerReferrerName: sourceTouched ? (visitSourceType === 'c2c' ? visitSourceReferrerName.trim() || null : null) : String(fromSource.customerReferrerName || '') || null,
        note: sourceTouched ? (visitSourceType === 'other' ? visitSourceNote.trim() || null : null) : String(fromSource.note || '') || null,
      };
      const sourceChanged = !!visit && (
        fromSource.sourceType !== toSource.sourceType ||
        String(fromSource.brokerId || '') !== String(toSource.brokerId || '') ||
        String(fromSource.brokerName || '') !== String(toSource.brokerName || '') ||
        String(fromSource.brokerAgencyName || '') !== String(toSource.brokerAgencyName || '') ||
        String(fromSource.customerReferrerName || '') !== String(toSource.customerReferrerName || '') ||
        (String(toSource.sourceType) === 'other' && String(fromSource.note || '') !== String(toSource.note || ''))
      );
      const sourceCorrection = sourceChanged ? { from: fromSource, to: toSource } : null;
      const visitArrival = toSource.sourceType === 'direct' ? 'walk-in' : toSource.sourceType;
      const sourceLabel = (source: Record<string, any>) => {
        if (source.sourceType === 'broker') return `Broker${source.brokerName ? ` ${source.brokerName}` : ''}${source.brokerAgencyName ? ` · ${source.brokerAgencyName}` : ''}`;
        if (source.sourceType === 'c2c') return `Customer referral${source.customerReferrerName ? ` · ${source.customerReferrerName}` : ''}`;
        if (source.sourceType === 'direct') return 'Direct / walk-in';
        if (source.sourceType === 'other') return `Other${source.note ? ` · ${source.note}` : ''}`;
        return 'Unknown';
      };
      const leadPayload: Record<string, any> = {
        activityCount: inc(1),
        visitCount: inc(1),
        lastActivityAt: serverTs(),
        updatedAt: serverTs(),
        updatedBy: by,
        nextFollowUpDate: isClosing ? null : ts(follow),
        nextFollowUpAt: followAt ? ts(followAt) : null,
      };
      const promptValues: Record<string, any> = {};
      if (selectedStatus && selectedStatus !== lead.status) leadPayload.status = selectedStatus;
      if (unitType && unitType !== lead.unitType && (!visit?.projectId || !lead.projectId || visit.projectId === lead.projectId)) leadPayload.unitType = unitType;
      if (!lead.projectId && visit?.projectId) {
        leadPayload.projectId = visit.projectId;
        leadPayload.projectName = visit.projectName || null;
      }
      const savePrompt = (id: string, value: any, label: string, type: string) => {
        if (value === '' || value === null || value === undefined || (Array.isArray(value) && !value.length)) return;
        const existing = lead.promptAnswers?.[id];
        const oldValue = Array.isArray(value) ? answerList(existing) : answer(existing);
        if (JSON.stringify(oldValue) === JSON.stringify(value)) return;
        leadPayload[`promptAnswers.${id}`] = {
          ...existing,
          value, type: existing?.type || type, label: existing?.label || label,
          statusId: selectedStatus || lead.status || null,
          answeredAt: existing?.answeredAt ? ts(toDate(existing.answeredAt) || new Date()) : serverTs(),
          answeredBy: existing?.answeredBy || by,
          ...(existing ? { editedAt: serverTs(), editedBy: by } : {}),
        };
        promptValues[id] = value;
      };
      savePrompt('legacy_budget', budget, 'Budget Fit', 'select');
      savePrompt('legacy_floorChoice', floorChoice, 'Floor Choice', 'select');
      savePrompt('legacy_unitChoice', unitChoices, 'Unit Choice', 'text');
      savePrompt('legacy_currentResidence', residence.trim(), 'Current Residence', 'text');
      savePrompt('legacy_occupation', occupation.trim(), 'Occupation', 'text');
      for (const prompt of statusPrompts) {
        const raw = (promptDraft[prompt.id] || '').trim();
        if (!raw) continue;
        const value = prompt.type === 'number' ? Number(raw) : prompt.type === 'date' ? ts(new Date(`${raw}T00:00:00`)) : raw;
        savePrompt(prompt.id, value, prompt.label, prompt.type);
      }
      let closeAnalysis: Record<string, any> | null = null;
      if (isClosing) {
        const resolvedReason = closeReason === 'Other' ? closeOtherReason.trim() : closeReason;
        const completed = closeReason === 'Completed';
        closeAnalysis = {
          primaryReason: resolvedReason, primaryReasonBase: closeReason,
          primaryOtherReason: closeReason === 'Other' ? closeOtherReason.trim() : null,
          secondaryReasons: completed ? [] : closeSecondaryReasons, decisionStageAuto: completed ? null : autoCloseStage, decisionStage: completed ? null : closeStage,
          interestedLocation: closeReason === 'Interested in Another Location' ? closeDetail.trim() : null,
          boughtAtLocation: closeReason === 'Purchased in Another Location' ? closeDetail.trim() : null,
          requiredType: closeReason === 'Need Other Type' ? closeRequiredType : null,
          requiredTypeLocation: closeReason === 'Need Other Type' ? closeDetail.trim() : null,
          budgetDetail: closeReason === 'Budget / Financial Issue' ? closeDetail : null,
          distancePreferredArea: closeReason === 'Distance Issue' ? closeDetail.trim() : null,
          competitorProject: closeReason === 'Loss due to Competitor' ? closeCompetitorProject.trim() : null,
          competitorBuilder: null,
          competitorPriceBand: closeReason === 'Loss due to Competitor' ? closeCompetitorPriceBand : null,
          competitorAdvantage: closeReason === 'Loss due to Competitor' ? closeCompetitorAdvantage : null,
          capturedBy: by, capturedAt: serverTs(),
        };
        Object.assign(leadPayload, {
          closedAt: lead.closedAt ? ts(toDate(lead.closedAt) || new Date()) : serverTs(),
          closeReason: resolvedReason, closePrimaryReason: closeReason,
          closePrimaryOtherReason: closeAnalysis.primaryOtherReason,
          closeSecondaryReasons: closeAnalysis.secondaryReasons, closeDecisionStageAuto: closeAnalysis.decisionStageAuto,
          closeDecisionStage: closeAnalysis.decisionStage,
          closeInterestedLocation: closeAnalysis.interestedLocation,
          closeBoughtAtLocation: closeAnalysis.boughtAtLocation,
          closeRequiredType: closeAnalysis.requiredType,
          closeRequiredTypeLocation: closeAnalysis.requiredTypeLocation,
          closeBudgetDetail: closeAnalysis.budgetDetail,
          closeDistancePreferredArea: closeAnalysis.distancePreferredArea,
          closeCompetitorProject: closeAnalysis.competitorProject,
          closeCompetitorBuilder: null,
          closeCompetitorPriceBand: closeAnalysis.competitorPriceBand,
          closeCompetitorAdvantage: closeAnalysis.competitorAdvantage,
          closedAnalysis: closeAnalysis, reopenWhatsappRequested: false,
        });
      }
      const activityPayload = {
        type: 'visit',
        projectId: projectId || null,
        projectName: visit?.projectName || lead.projectName || null,
        unitType: unitType || lead.unitType || null,
        callOutcome: null,
        visitWith: visit?.visitWith || null,
        visitWithOther: visit?.visitWithOther || null,
        visitArrival,
        visitArrivalBrokerId: toSource.sourceType === 'broker' ? toSource.brokerId : null,
        visitArrivalBrokerName: toSource.sourceType === 'broker' ? toSource.brokerName : null,
        visitArrivalBrokerAgencyName: toSource.sourceType === 'broker' ? toSource.brokerAgencyName : null,
        visitArrivalC2cName: toSource.sourceType === 'c2c' ? toSource.customerReferrerName : null,
        visitArrivalOtherNote: toSource.sourceType === 'other' ? toSource.note : null,
        visitSourceCorrection: sourceCorrection,
        ...(sourceCorrection ? {
          refByFromType: fromSource.sourceType === 'direct' ? 'none' : fromSource.sourceType,
          refByFromId: fromSource.brokerId || null,
          refByFromName: fromSource.brokerName || fromSource.customerReferrerName || null,
          refByFromAgency: fromSource.brokerAgencyName || null,
          refByFromRefName: fromSource.customerReferrerName || null,
          refByToType: toSource.sourceType === 'direct' ? 'none' : toSource.sourceType,
          refByToId: toSource.brokerId || toSource.customerReferrerLeadId || null,
          refByToName: toSource.brokerName || toSource.customerReferrerName || null,
          refByToAgency: toSource.brokerAgencyName || null,
          refByToRefName: toSource.customerReferrerName || null,
        } : {}),
        // Points, plus anything still typed in the box.
        notes: (() => {
          const all = [...points];
          const pending = note.trim().replace(/\s+/g, ' ');
          if (pending && !all.some((p) => p.toLowerCase() === pending.toLowerCase())) all.push(pending);
          if (sourceCorrection) all.push(`Visit source corrected after reception: ${sourceLabel(fromSource)} → ${sourceLabel(toSource)}.`);
          return all.length ? all : null;
        })(),
        promptAnswers: Object.keys(promptValues).length ? promptValues : null,
        statusFrom: leadPayload.status ? lead.status || null : null,
        statusTo: leadPayload.status || null,
        followUpFrom: lead.nextFollowUpDate ? ts(toDate(lead.nextFollowUpDate) || new Date()) : null,
        followUpFromAt: lead.nextFollowUpAt ? ts(toDate(lead.nextFollowUpAt) || new Date()) : null,
        followUpDate: isClosing ? null : ts(follow),
        followUpAt: followAt ? ts(followAt) : null,
        closeAnalysis,
        byEmail: by,
        at: serverTs(),
      };
      await httpsCallable(getFunctions(app), 'logLeadActivity')({
        leadId,
        leadPayload,
        activityPayload,
        receptionAssignToMe: assignToMe === true,
      });
      // Interest = rating, logged like the drawer's rating change.
      const ratingFrom = typeof lead.rating === 'number' ? lead.rating : null;
      if (canRate && rating !== null && rating !== ratingFrom) {
        await updateDoc(doc(db, 'leads', leadId), { rating, ratingUpdatedAt: serverTimestamp(), ratingUpdatedBy: by });
        await addDoc(collection(db, 'leads', leadId, 'activities'), {
          type: 'internal', event: 'rating', ratingFrom, ratingTo: rating, byEmail: by, at: serverTimestamp(),
        });
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const waited = visit ? (toDate(visit.takenAt) && toDate(visit.checkedInAt)
    ? Math.round(((toDate(visit.takenAt) as Date).getTime() - (toDate(visit.checkedInAt) as Date).getTime()) / 60000) : null) : null;
  const chips = visit ? [
    visit.projectName,
    visit.visitWith ? (visit.visitWith === 'alone' ? 'Alone' : `${visit.visitWith === 'family' ? 'Family' : visit.visitWith === 'friend' ? 'Relative' : visit.visitWithOther || 'Others'}${visit.peopleCount > 1 ? ` · ${visit.peopleCount} people` : ''}`) : null,
  ].filter(Boolean) as string[] : [];
  const visitSourceBrokerOptions = useMemo(() => {
    if (!visitSourceBroker || visitSourceBrokers.some((item) => item.id === visitSourceBroker.id)) return visitSourceBrokers;
    return [visitSourceBroker, ...visitSourceBrokers];
  }, [visitSourceBroker, visitSourceBrokers]);
  const receptionSourceLabel = visit?.cameVia === 'broker'
    ? `Broker${visit.brokerName ? ` · ${visit.brokerName}` : ''}`
    : visit?.cameVia === 'c2c'
      ? `Customer referral${visit.c2cName ? ` · ${visit.c2cName}` : ''}`
      : visit?.cameVia === 'walk-in' ? 'Direct / walk-in' : 'Not recorded';
  const sourcePillLabel = visitSourceType === 'broker'
    ? `Via Broker${visitSourceBroker?.brokerName ? ` ${visitSourceBroker.brokerName}` : ''}`
    : visitSourceType === 'c2c'
      ? `C2C${visitSourceReferrerName ? ` ${visitSourceReferrerName}` : ''}`
      : visitSourceType === 'direct' ? 'Walk-in'
        : visitSourceType === 'other' ? `Other${visitSourceNote ? ` · ${visitSourceNote}` : ''}` : 'Unknown';
  const todayYmd = dateInput(new Date());
  const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const calendarDays = [
    ...Array.from({ length: monthStart.getDay() }, () => null),
    ...Array.from({ length: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate() }, (_, index) => index + 1),
  ];
  const canShowPreviousMonth = monthStart > new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: FONT, color: '#1a1a1a' }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 2.5 }, pb: 1.5, borderBottom: '1px solid #eee8de' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="h2" sx={{ fontFamily: FONT, fontSize: { xs: 17, sm: 19 }, fontWeight: 700, flex: 1 }}>
            Finish visit · {String(lead?.name || visit?.name || '')}
          </Typography>
          <Box component="button" type="button" aria-label="Close" onClick={onClose}
            sx={{ width: 36, height: 36, borderRadius: '50%', border: `1px solid #e8e3da`, bgcolor: '#fff', cursor: 'pointer', fontSize: 16 }}>×</Box>
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: MUTED }}>
          {[
            waited !== null ? `Waited ${waited} min` : null,
            visit?.takenAt ? `meeting ${minutesSince(visit.takenAt)} min` : null,
            ownerName ? `assigned to ${ownerName}` : 'unassigned',
          ].filter(Boolean).join(' · ')}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {(chips.length > 0 || !!visit) && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {chips.map((c) => (
              <Box key={c} sx={{ fontSize: 12.5, bgcolor: '#f3efe8', borderRadius: 999, px: 1.5, py: 0.75 }}>{c}</Box>
            ))}
            {visit && <>
              <Box component="button" type="button" onClick={() => setSourceEditing((value) => !value)}
                aria-label={`Edit visit source: ${sourcePillLabel}`} aria-expanded={sourceEditing} aria-controls="finish-visit-source-options"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, bgcolor: '#f3efe8', color: DARK, border: `1px solid ${sourceEditing ? GOLD : 'transparent'}`, borderRadius: 999, px: 1.5, py: 0.75, cursor: 'pointer', '&:hover': { borderColor: GOLD, bgcolor: '#f8f4ec' } }}>
                {sourcePillLabel}<EditOutlinedIcon sx={{ fontSize: 15, color: GOLD }} />
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: MUTED }}>visit source</Typography>
            </>}
          </Box>
        )}

        {visit && sourceEditing && (
          <Box id="finish-visit-source-options" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box>
              <Label>Came via *</Label>
              <Box role="group" aria-label="Visit source" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CAME_VIA_OPTIONS.map((option) => {
                  const value: VisitSourceType = option.key === 'walk-in' ? 'direct' : option.key;
                  return <Pill key={option.key} selected={visitSourceType === value} onClick={() => { setVisitSourceType(value); setSourceTouched(true); }}>{option.label}</Pill>;
                })}
              </Box>
            </Box>
            {visitSourceType !== 'direct' && visitSourceType !== 'unknown' && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {visitSourceType === 'broker' && (
              <Autocomplete<VisitBrokerOption, false, false, false>
                options={visitSourceBrokerOptions}
                value={visitSourceBroker}
                onChange={(_, value) => { setVisitSourceBroker(value); setSourceTouched(true); }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No brokers available"
                renderInput={(params) => <TextField {...params} size="small" label="Broker *" placeholder="Search broker or agency" />}
              />
            )}
            {visitSourceType === 'c2c' && (
              <TextField size="small" fullWidth label="C2C reference name *" value={visitSourceReferrerName}
                onChange={(event) => { setVisitSourceReferrerName(event.target.value); setSourceTouched(true); }} />
            )}
            {visitSourceType === 'other' && (
              <TextField size="small" fullWidth label="Other source *" value={visitSourceNote}
                onChange={(event) => { setVisitSourceNote(event.target.value); setSourceTouched(true); }} placeholder="Add a short explanation" />
            )}
            </Box>}
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: MUTED }}>
              Front desk recorded {receptionSourceLabel}. Changes are saved in visit history when you finish.
            </Typography>
          </Box>
        )}

        {hasPreviousAnswers && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: MUTED }}>
          Previously recorded answers are selected below. Confirm or change them for this visit.
        </Typography>}

        <Box>
          <Label previous={!!previousUnitType && unitType === previousUnitType}>Unit type *</Label>
          <Box role="group" aria-label="Unit type" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {unitTypes.map((u) => <Pill key={u} selected={unitType === u} onClick={() => setUnitType(u)}>{u}</Pill>)}
            {!unitTypes.length && <Typography sx={{ fontSize: 13, color: MUTED }}>No unit types configured for this project.</Typography>}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: canRate ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 2 }}>
          <Box>
            <Label previous={!!previousBudget && budget === previousBudget}>Budget fit</Label>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['In Budget', 'Under Budget'].map((b) => <Pill key={b} selected={budget === b} onClick={() => setBudget(b)}>{b}</Pill>)}
            </Box>
          </Box>
          {canRate && (
            <Box>
              <Label previous={typeof lead?.rating === 'number' && rating === lead.rating}>Interest</Label>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {INTEREST.map((r) => <Pill key={r.key} selected={rating === r.key} color={r.color} onClick={() => setRating(r.key)}>{r.label}</Pill>)}
              </Box>
            </Box>
          )}
        </Box>

        <Box>
          <Label previous={!!previousFloorChoice && floorChoice === previousFloorChoice}>Floor choice</Label>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {['Lower', 'Middle', 'Higher', 'Any'].map((value) =>
              <Pill key={value} selected={floorChoice === value} onClick={() => setFloorChoice(value)}>{value}</Pill>)}
          </Box>
        </Box>

        <Box>
          <Label htmlFor="fv-unit-number" previous={previousUnitChoices.length > 0 &&
            previousUnitChoices.length === unitChoices.length && previousUnitChoices.every((value) => unitChoices.includes(value))}>Unit number(s)</Label>
          <Autocomplete<string, false, false, false>
            options={unitNumbers.filter((value) => !unitChoices.includes(value))}
            value={null}
            inputValue={unitSearch}
            onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') setUnitSearch(value); }}
            onChange={(_, value) => {
              if (value) setUnitChoices((list) => list.includes(value) ? list : [...list, value]);
              setUnitSearch('');
            }}
            filterOptions={(options, state) => state.inputValue.trim().length < 2 ? [] : options.filter((value) => value.toLowerCase().includes(state.inputValue.trim().toLowerCase()))}
            noOptionsText={unitSearch.trim().length < 2 ? 'Type at least 2 characters to search units' : 'No matching unit numbers'}
            renderInput={(params) => <TextField {...params} id="fv-unit-number" placeholder={unitNumbers.length ? 'Search unit number' : 'No units configured for this project'}
              sx={{ '& .MuiOutlinedInput-root': { minHeight: 44, borderRadius: 2.5, fontFamily: FONT, fontSize: 14, '& fieldset': { borderColor: LINE } } }} />}
          />
          {unitChoices.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {unitChoices.map((value) => <Box key={value} component="button" type="button" title="Remove unit"
              onClick={() => setUnitChoices((list) => list.filter((item) => item !== value))}
              sx={{ px: 1.25, py: 0.5, border: `1px solid ${LINE}`, borderRadius: 1.5, bgcolor: '#faf8f4', cursor: 'pointer' }}>
              {value} ×
            </Box>)}
          </Box>}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          <Box><Label htmlFor="fv-residence" previous={!!previousResidence && residence === previousResidence}>Current residence</Label><Box component="input" id="fv-residence" value={residence} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setResidence(event.target.value)} sx={fieldSx} /></Box>
          <Box><Label htmlFor="fv-occupation" previous={!!previousOccupation && occupation === previousOccupation}>Occupation</Label><Box component="input" id="fv-occupation" value={occupation} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOccupation(event.target.value)} sx={fieldSx} /></Box>
        </Box>

        <Box>
          <Label htmlFor="fv-note">What happened</Label>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box component="input" id="fv-note" value={note} placeholder="Type a point and press Enter"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addPoint(note);
                  setNote('');
                }
              }}
              sx={{ ...fieldSx, flex: 1, minWidth: 0 }} />
            <Box component="button" type="button" disabled={!note.trim()} onClick={() => { addPoint(note); setNote(''); }}
              sx={{ minHeight: 44, px: 2, borderRadius: 2.5, border: `1px solid ${GOLD}`, bgcolor: '#fff', color: GOLD, fontFamily: FONT, fontWeight: 700, fontSize: 14, cursor: 'pointer', '&:disabled': { opacity: 0.45, cursor: 'default' } }}>
              Add
            </Box>
          </Box>
          {points.length > 0 && (
            <Box component="ol" sx={{ m: 0, mt: 1, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {points.map((p, i) => (
                <Box component="li" key={p} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#faf8f4', border: '1px solid #eee8de', borderRadius: 2, pl: 1.25, pr: 0.5, py: 0.25 }}>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: MUTED, width: 18 }}>{i + 1}.</Typography>
                  <Box component="button" type="button" title="Edit this point"
                    onClick={() => { setPoints((prev) => prev.filter((x) => x !== p)); setNote(p); }}
                    sx={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', bgcolor: 'transparent', p: 0.5, cursor: 'text', fontFamily: FONT, fontSize: 14, color: '#1a1a1a' }}>
                    {p}
                  </Box>
                  <Box component="button" type="button" aria-label={`Remove point ${i + 1}`}
                    onClick={() => setPoints((prev) => prev.filter((x) => x !== p))}
                    sx={{ width: 32, height: 32, border: 'none', bgcolor: 'transparent', color: MUTED, fontSize: 16, cursor: 'pointer', borderRadius: '50%', '&:hover': { bgcolor: '#f0ece4' } }}>
                    ✕
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Label>Status</Label>
            <Box role="group" aria-label="Status" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {statusChoices.map((choice) => <Pill key={choice.id || 'keep'} selected={selectedStatus === choice.id} onClick={() => setStatus(choice.id)}>{choice.label}</Pill>)}
            </Box>
          </Box>
          {!isClosing && <Box component="section" aria-label="Next follow-up">
            <Label>Next follow-up *</Label>
            <Box id="fv-follow" aria-label="Choose next follow-up date" sx={{ border: `1px solid ${LINE}`, borderRadius: 2.5, p: { xs: 1.25, sm: 1.75 }, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                  <Box component="button" type="button" aria-label="Previous month" disabled={!canShowPreviousMonth}
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                    sx={{ width: 34, height: 34, border: `1px solid ${LINE}`, borderRadius: '50%', bgcolor: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer', '&:disabled': { opacity: 0.3, cursor: 'default' } }}>‹</Box>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 14 }}>
                    {new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' }).format(monthStart)}
                  </Typography>
                  <Box component="button" type="button" aria-label="Next month"
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                    sx={{ width: 34, height: 34, border: `1px solid ${LINE}`, borderRadius: '50%', bgcolor: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>›</Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, textAlign: 'center' }}>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => <Typography key={day} sx={{ fontSize: 11, fontWeight: 700, color: MUTED, py: 0.5 }}>{day}</Typography>)}
                  {calendarDays.map((day, index) => {
                    const value = day ? dateInput(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day)) : '';
                    const disabled = !!day && value <= todayYmd;
                    return day ? <Box key={value} component="button" type="button" disabled={disabled} aria-label={displayDate(value)} aria-pressed={followUp === value}
                      onClick={() => setFollowUp(value)}
                      sx={{ width: 36, height: 36, justifySelf: 'center', borderRadius: '50%', border: 'none', bgcolor: followUp === value ? GOLD : 'transparent', color: disabled ? '#b8b8b8' : followUp === value ? '#fff' : DARK, fontWeight: followUp === value ? 700 : 400, cursor: disabled ? 'default' : 'pointer', '&:hover': disabled ? {} : { bgcolor: followUp === value ? GOLD : '#f3efe8' } }}>{day}</Box>
                      : <Box key={`empty-${index}`} />;
                  })}
                </Box>
              <Box sx={{ mt: 1, pt: 1.25, borderTop: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: GOLD }}>Selected: {displayDate(followUp)}</Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }}>Today and past dates unavailable</Typography>
              </Box>
            <Box component="label" sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, fontSize: 13 }}>
              <input type="checkbox" checked={!!followUpTime} onChange={(event) => setFollowUpTime(event.target.checked ? (followUpTime || '10:00') : '')} />
              Set reminder time
            </Box>
            {followUpTime && <Box component="input" type="time" value={followUpTime}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFollowUpTime(event.target.value)}
              sx={{ mt: 1, width: '100%', height: 42, px: 1.25, boxSizing: 'border-box', border: `1px solid ${LINE}`, borderRadius: 1.5 }} />}
            </Box>
          </Box>}
        </Box>

        {statusPrompts.map((prompt) => (
          <Box key={prompt.id}>
          <TextField fullWidth size="small" label={prompt.label}
            type={prompt.type === 'date' ? 'date' : prompt.type === 'number' ? 'number' : 'text'}
            select={prompt.type === 'select'} SelectProps={prompt.type === 'select' ? { native: true } : undefined}
            InputLabelProps={prompt.type === 'date' ? { shrink: true } : undefined}
            value={promptDraft[prompt.id] || ''}
            onChange={(event) => setPromptDraft((draft) => ({ ...draft, [prompt.id]: event.target.value }))}>
            {prompt.type === 'select' && <option value="">Choose answer</option>}
            {prompt.type === 'select' && (prompt.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
          </TextField>
          {lead?.promptAnswers?.[prompt.id] != null && promptDraft[prompt.id] ===
            (prompt.type === 'date'
              ? (toDate(lead.promptAnswers[prompt.id]?.value ?? lead.promptAnswers[prompt.id])
                ? dateInput(toDate(lead.promptAnswers[prompt.id]?.value ?? lead.promptAnswers[prompt.id])!) : '')
              : answer(lead.promptAnswers[prompt.id])) && !!promptDraft[prompt.id] &&
            <Typography sx={{ mt: 0.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, color: GOLD }}>Previously recorded</Typography>}
          </Box>
        ))}

        {isClosing && <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Box>
            <Label htmlFor="fv-close-reason">Primary reason *</Label>
            <Box component="select" id="fv-close-reason" value={closeReason}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => { setCloseReason(event.target.value); setCloseDetail(''); }} sx={fieldSx}>
              <option value="">Choose reason</option>
              {CLOSE_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </Box>
          </Box>
          {closeReason === 'Other' && <TextField fullWidth size="small" label="Other reason *" value={closeOtherReason}
            onChange={(event) => setCloseOtherReason(event.target.value)} />}
          {closeReason && closeReason !== 'Completed' && <Box>
            <Label htmlFor="fv-close-stage">Decision stage *</Label>
            <Box component="select" id="fv-close-stage" value={closeStage}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => { setCloseStage(event.target.value); setCloseStageTouched(true); }} sx={fieldSx}>
              <option value="">Choose stage</option>
              {CLOSE_STAGES.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
            </Box>
          </Box>}
          {closeReason && closeReason !== 'Completed' && <Box>
            <Label>Other reasons (optional)</Label>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {CLOSE_SECONDARY_REASONS.filter((reason) => reason !== closeReason).map((reason) =>
                <Pill key={reason} selected={closeSecondaryReasons.includes(reason)} onClick={() =>
                  setCloseSecondaryReasons((list) => list.includes(reason) ? list.filter((item) => item !== reason) : [...list, reason])}>{reason}</Pill>)}
            </Box>
          </Box>}
          {closeReason === 'Need Other Type' && <Box>
            <Label htmlFor="fv-close-required-type">Required type *</Label>
            <Box component="select" id="fv-close-required-type" value={closeRequiredType}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCloseRequiredType(event.target.value)} sx={fieldSx}>
              <option value="">Choose type</option>
              {CLOSE_REQUIRED_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </Box>
          </Box>}
          {['Interested in Another Location', 'Purchased in Another Location', 'Need Other Type', 'Distance Issue'].includes(closeReason) &&
            <TextField fullWidth size="small" label={closeReason === 'Distance Issue' ? 'Preferred area *' : 'Location *'}
              value={closeDetail} onChange={(event) => setCloseDetail(event.target.value)} />}
          {closeReason === 'Budget / Financial Issue' && <Box>
            <Label htmlFor="fv-close-budget">Budget issue *</Label>
            <Box component="select" id="fv-close-budget" value={closeDetail}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCloseDetail(event.target.value)} sx={fieldSx}>
              <option value="">Choose detail</option>
              {CLOSE_BUDGET_DETAILS.map((detail) => <option key={detail} value={detail}>{detail}</option>)}
            </Box>
          </Box>}
          {closeReason === 'Loss due to Competitor' && <>
            <TextField fullWidth size="small" label="Competitor project *" value={closeCompetitorProject}
              onChange={(event) => setCloseCompetitorProject(event.target.value)} />
            <Box>
              <Label htmlFor="fv-close-price-band">Competitor price band *</Label>
              <Box component="select" id="fv-close-price-band" value={closeCompetitorPriceBand}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCloseCompetitorPriceBand(event.target.value)} sx={fieldSx}>
                <option value="">Choose price band</option>
                {CLOSE_COMP_PRICE_BANDS.map((band) => <option key={band} value={band}>{band}</option>)}
              </Box>
            </Box>
            <Box>
              <Label htmlFor="fv-close-advantage">Main competitor advantage *</Label>
              <Box component="select" id="fv-close-advantage" value={closeCompetitorAdvantage}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setCloseCompetitorAdvantage(event.target.value)} sx={fieldSx}>
                <option value="">Choose advantage</option>
                {CLOSE_COMP_ADVANTAGES.map((advantage) => <option key={advantage} value={advantage}>{advantage}</option>)}
              </Box>
            </Box>
          </>}
        </Box>}

        {askOwner && (
          <Box sx={{ bgcolor: '#faf8f4', border: '1px solid #eee8de', borderRadius: 3, p: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, flex: '1 1 180px' }}>
              Lead is assigned to <b>{ownerName}</b>. Assign it to you?
            </Typography>
            <Pill selected={assignToMe === true} onClick={() => setAssignToMe(true)}>Assign to me</Pill>
            <Pill selected={assignToMe === false} onClick={() => setAssignToMe(false)}>Keep</Pill>
          </Box>
        )}

        {error && <Typography role="alert" sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: '#b3261e' }}>{error}</Typography>}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: { xs: 2.5, sm: 2.5 }, borderTop: '1px solid #eee8de', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {leadId && (
          <Box component="button" type="button" onClick={() => onOpenLead(leadId)}
            sx={{ border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 13, color: '#7a5424', textDecoration: 'underline', flexShrink: 0 }}>
            Open full lead
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        <Box component="button" type="button" disabled={saving || !lead} onClick={save}
          sx={{ minHeight: 48, px: 3.5, borderRadius: 3, border: 'none', bgcolor: DARK, color: '#fff', fontFamily: FONT, fontWeight: 700, fontSize: 15, cursor: 'pointer', width: { xs: '100%', sm: 'auto' }, '&:disabled': { opacity: 0.6, cursor: 'default' } }}>
          {saving ? 'Saving…' : 'Save · finish visit'}
        </Box>
      </Box>
    </Box>
  );

  if (isPhone) {
    return (
      <SwipeableDrawer anchor="bottom" open={open} onClose={onClose} onOpen={() => undefined} disableSwipeToOpen
        PaperProps={{ sx: { borderRadius: '20px 20px 0 0', maxHeight: '92vh' } }}>
        <Box sx={{ width: 40, height: 4, bgcolor: '#ddd', borderRadius: 2, mx: 'auto', mt: 1.25, mb: 0.5, flexShrink: 0 }} />
        {body}
      </SwipeableDrawer>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { width: 560, borderRadius: 4.5, maxHeight: '90vh' } }}>
      {body}
    </Dialog>
  );
}

